import React, { useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import TablePagination from "@mui/material/TablePagination";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import { ReactComponent as MoreHorizIcon } from "../../../assets/images/menukb.svg";
import NoDataFound from "../Traffic/NoDataFound";
import { colorCodesForAvatar } from "../../../constants/index";
import { chracterPosition } from "../../../utils/index";

const tableCheckboxIconSx = {
  width: 16,
  height: 16,
  borderRadius: "4px",
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 0,
  flexShrink: 0,
  "& svg": { display: "block", flexShrink: 0 },
};

function TableCheckboxTick() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none">
      <path
        d="M6.5 0.5L2.74922 4.25078L0.5 2.00031"
        stroke="#148DFD"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TableCheckboxUncheckedIcon() {
  return (
    <Box
      component="span"
      sx={{
        ...tableCheckboxIconSx,
        border: "1px solid #D5DCE1",
        backgroundColor: "#FFF",
        boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
      }}
    />
  );
}

function TableCheckboxCheckedIcon() {
  return (
    <Box
      component="span"
      sx={{
        ...tableCheckboxIconSx,
        border: "1px solid #148DFD",
        backgroundColor: "#FFF",
        boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
      }}
    >
      <TableCheckboxTick />
    </Box>
  );
}

const CHECKBOX_GAP_PX = 16;
/** Default horizontal bleed for People list pages inside `.Ticket-container-render` (24px padding). */
export const PEOPLE_TABLE_HORIZONTAL_BLEED = 24;

const BlueRoundedCheckbox = styled(Checkbox)(() => ({
  padding: 0,
  width: 16,
  height: 16,
  margin: 0,
  "&.MuiCheckbox-root": {
    color: "transparent",
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .MuiSvgIcon-root": {
    width: 16,
    height: 16,
    fontSize: 16,
    margin: 0,
  },
  "& > span": {
    display: "inline-flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",
    width: "16px !important",
    height: "16px !important",
    margin: "0 !important",
    lineHeight: 0,
  },
  "&:hover": { backgroundColor: "transparent" },
}));

export function NameCell({ name, avatar, subtitle }) {
  const letter = (name || "").substring(0, 1).toUpperCase();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        overflow: "hidden",
        minWidth: 0,
        width: "100%",
      }}
    >
      {avatar ? (
        <Avatar src={avatar} alt={name} sx={{ width: 32, height: 32, flexShrink: 0 }} />
      ) : (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: 13,
            flexShrink: 0,
            bgcolor: colorCodesForAvatar[chracterPosition(letter)] || "#405261",
          }}
        >
          {letter}
        </Avatar>
      )}
      <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
        <Typography
          sx={{
            color: "#061522",
            fontSize: 14,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Typography>
        {subtitle ? (
          <Typography
            sx={{
              color: "#405261",
              fontSize: 12,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * Generic People Table.
 * Props:
 *  - columns: Array<{ key, label, width?, render?(row) => ReactNode, align? }>
 *  - rows: Array<Object> (each needs `id`)
 *  - selected / setSelected
 *  - page / rowsPerPage / onChangePage / totalCount
 *  - actionMenu: Array<{ key, label, danger?, hidden?(row) }>
 *  - onAction(type, row)
 *  - entityLabel: "Customers" | "Teams" | ... (for pagination footer)
 *  - horizontalBleed: px to extend row backgrounds into parent padding (e.g. 24 for Ticket-container-render)
 */
export default function PeopleTable({
  columns,
  rows,
  selected = [],
  setSelected = () => {},
  page = 0,
  rowsPerPage = 10,
  onChangePage,
  totalCount,
  actionMenu = [],
  onAction,
  entityLabel = "Items",
  onRowClick,
  highlightedRowId,
  selectable = true,
  horizontalBleed = 0,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const open = Boolean(anchorEl);

  const isServerSide = typeof totalCount === "number";
  const visibleRows = React.useMemo(() => {
    if (isServerSide) return rows;
    return (rows || []).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [rows, page, rowsPerPage, isServerSide]);

  const visibleIds = visibleRows.map((r) => r.id);
  const selectedOnPage = selectable ? selected.filter((id) => visibleIds.includes(id)) : [];
  const allSelected = visibleRows.length > 0 && selectedOnPage.length === visibleRows.length;
  const someSelected = selectedOnPage.length > 0 && selectedOnPage.length < visibleRows.length;
  const displayCount = isServerSide ? totalCount : (rows || []).length;

  const toggleAll = (e) => {
    if (e.target.checked) {
      setSelected((prev) => Array.from(new Set([...prev, ...visibleIds])));
    } else {
      setSelected((prev) => prev.filter((id) => !visibleIds.includes(id)));
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleMenuOpen = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setMenuRow(row);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuExited = () => {
    setMenuRow(null);
  };

  const visibleMenuItems = React.useMemo(() => {
    if (!menuRow) return [];
    return actionMenu.filter((item) => !item.hidden?.(menuRow));
  }, [actionMenu, menuRow]);
  const call = (type) => {
    onAction?.(type, menuRow);
    handleMenuClose();
  };

  // Grid: optional checkbox + 16px gap before first column
  const gridTemplate = [
    ...(selectable ? [`${16 + CHECKBOX_GAP_PX}px`] : []),
    ...columns.map((c) => c.width || "minmax(80px, 1fr)"),
    "44px",
  ].join(" ");

  const checkboxCellSx = {
    display: "flex",
    alignItems: "center",
    width: 16,
    boxSizing: "content-box",
    pr: `${CHECKBOX_GAP_PX}px`,
  };

  const uncheckedIconEl = <TableCheckboxUncheckedIcon />;
  const checkedIconEl = <TableCheckboxCheckedIcon />;
  const bleed = Number(horizontalBleed) || 0;
  const rowPadX = bleed > 0 ? `${bleed}px` : undefined;

  return (
    <Box
      sx={{
        mt: 0,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
        ...(bleed > 0
          ? {
              mx: `-${bleed}px`,
              width: `calc(100% + ${bleed * 2}px)`,
            }
          : {}),
      }}
    >
      {/* Inner scroll (same pattern as ArticlesTable): body scrolls, pagination sits below. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: "auto",
          scrollbarGutter: "stable",
          // Breathing room above the footer (matches ticket TableContainer margin pattern).
          pb: "16px",
        }}
      >
        {/* Header row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridTemplate,
            alignItems: "center",
            height: 48,
            position: "sticky",
            top: 0,
            background: "#fff",
            zIndex: 1,
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            borderBottom: "1px solid #EEF2F6",
            boxSizing: "border-box",
            ...(rowPadX ? { px: rowPadX } : {}),
          }}
        >
          {selectable ? (
          <Box sx={checkboxCellSx}>
            <BlueRoundedCheckbox
              indeterminate={someSelected}
              checked={allSelected}
              onChange={toggleAll}
              icon={uncheckedIconEl}
              checkedIcon={checkedIconEl}
              disableRipple
            />
          </Box>
          ) : null}
          {columns.map((col) => (
            <Typography
              key={col.key}
              sx={{
                color: "#405261",
                fontSize: 12,
                fontWeight: 500,
                textAlign: col.align || "left",
                pr: col.cellPr ?? 1,
                pl: col.cellPl ?? 0,
              }}
            >
              {col.label}
            </Typography>
          ))}
          <Box sx={{ minWidth: 0, display: "flex", justifyContent: "flex-end", pr: 0.5 }} />
        </Box>

        {visibleRows.length === 0 ? (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <NoDataFound
              containerStyle={{ height: "59vh" }}
              title={`No ${entityLabel.toLowerCase()} found`}
              description="This segment is empty. Try another one or come back later"
            />
          </Box>
        ) : (
          visibleRows.map((row) => {
            const isHighlighted = highlightedRowId != null && row.id === highlightedRowId;
            return (
            <Box
              key={row.id}
              onClick={(e) => {
                if (
                  e.target.closest(".MuiCheckbox-root") ||
                  e.target.closest(".MuiIconButton-root")
                ) {
                  return;
                }
                onRowClick?.(row);
              }}
              sx={{
                display: "grid",
                gridTemplateColumns: gridTemplate,
                alignItems: "center",
                minHeight: 64,
                borderBottom: "1px solid #EEF2F6",
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                cursor: onRowClick ? "pointer" : "default",
                backgroundColor: isHighlighted ? "#F7F8F9" : "transparent",
                "&:hover": { backgroundColor: "#F7F8F9" },
                boxSizing: "border-box",
                ...(rowPadX ? { px: rowPadX } : {}),
              }}
            >
              {selectable ? (
              <Box sx={checkboxCellSx}>
                <BlueRoundedCheckbox
                  checked={selected.includes(row.id)}
                  onChange={() => toggleOne(row.id)}
                  icon={uncheckedIconEl}
                  checkedIcon={checkedIconEl}
                  disableRipple
                />
              </Box>
              ) : null}
              {columns.map((col) => (
                <Box
                  key={col.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    pr: col.cellPr ?? 1,
                    pl: col.cellPl ?? 0,
                    minWidth: 0,
                    overflow: col.cellOverflow || "hidden",
                    justifyContent:
                      col.align === "center"
                        ? "center"
                        : col.align === "right"
                        ? "flex-end"
                        : "flex-start",
                  }}
                >
                  {col.render ? (
                    col.render(row)
                  ) : (
                    <Typography
                      sx={{
                        color: col.textColor || "#405261",
                        fontSize: 14,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                        width: "100%",
                        maxWidth: "100%",
                      }}
                    >
                      {row[col.key]}
                    </Typography>
                  )}
                </Box>
              ))}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  minWidth: 0,
                  pr: 0.5,
                }}
              >
                {actionMenu.length > 0 && (
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, row)}
                    size="small"
                    sx={{ p: 0.5 }}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
            );
          })
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        TransitionProps={{ onExited: handleMenuExited }}
        elevation={0}
        marginThreshold={0}
        // style={{left:'1034px'}}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        MenuListProps={{ disablePadding: true }}
        PaperProps={{
          sx: {
            width: "184px",
            borderRadius: "8px",
            border: "1px solid #D5DCE1",
            background: "#FFF",
            boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
            mt: "4px",
            overflow: "hidden",
            py: "8px",
            "& .MuiMenuItem-root": {
              fontFamily: "Inter",
              fontSize: "14px",
              fontWeight: 400,
              px: "16px",
              "&:hover": { background: "#F9FAFA" },
            },
          },
        }}
      >
        {visibleMenuItems.map((item) => (
          <MenuItem
            key={item.key}
            onClick={() => call(item.key)}
            sx={{
              color: item.danger ? "#FF4D4F !important" : "#405261 !important",
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {displayCount >= rowsPerPage && (
        <Box
          sx={{
            width: "100%",
            background: "#fff",
            borderTop: "1px solid #E5E9EC",
            flexShrink: 0,
            zIndex: 2,
            boxSizing: "border-box",
            py: "16px",
            ...(rowPadX ? { px: rowPadX } : {}),
          }}
        >
          <TablePagination
            component="div"
            count={displayCount}
            page={page}
            onPageChange={(_, newPage) => onChangePage?.(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
            labelDisplayedRows={({ from, to }) => (
              <Typography
                sx={{ color: "#405261", fontFamily: "Inter", fontSize: 14, fontWeight: 400 }}
              >
                {entityLabel} {from}-{to}
              </Typography>
            )}
            ActionsComponent={(props) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                <Button
                  onClick={(e) => props.onPageChange(e, props.page - 1)}
                  disabled={props.page === 0}
                  sx={{
                    width: 128,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: "8px",
                    border: "1px solid #D5DCE1",
                    background: "#FFF",
                    color: "#405261",
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: "none",
                    "&.Mui-disabled": {
                      color: "#93A1B0",
                      border: "1px solid #C5CED6",
                      bgcolor: "#F9FAFA",
                    },
                  }}
                >
                  Previous
                </Button>
                <Button
                  onClick={(e) => props.onPageChange(e, props.page + 1)}
                  disabled={props.page >= Math.ceil(props.count / rowsPerPage) - 1}
                  sx={{
                    width: 88,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: "8px",
                    border: "1px solid #D5DCE1",
                    background: "#FFF",
                    color: "#405261",
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: "none",
                    "&.Mui-disabled": {
                      color: "#93A1B0",
                      border: "1px solid #C5CED6",
                      bgcolor: "#F9FAFA",
                    },
                  }}
                >
                  Next
                </Button>
              </Box>
            )}
            sx={{
              ".MuiTablePagination-toolbar": {
                padding: 0,
                minHeight: "unset",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "nowrap",
              },
              ".MuiTablePagination-spacer": { display: "none" },
              ".MuiTablePagination-selectLabel": { display: "none" },
              ".MuiTablePagination-select": { display: "none" },
              ".MuiTablePagination-selectIcon": { display: "none" },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
